import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { IWatrReimbursement, IWageAndTravelVerifyValidation, IWatrRequest } from '@nkr/models/index';
import { FormControl, Button, FormGroup, ControlLabel, InputGroup, Panel, Alert } from 'react-bootstrap';
import { FileDocumentType } from '@nkr/enums/document-type';
import WatrInfoAccordions from '../accordion';
import styled from 'styled-components';
import Header from '../header';
import VerificationStepTracker from '../verification-step-tracker';
import { DonorShieldContext } from '../context';
import SsnInput from '../common/ssn-input';
import ConnectedDevMockDocsOffer from '../dev-mock-docs/ConnectedDevMockDocsOffer';
import { formatMoneyInput, parseMoneyInput } from '../common/money-input';
import { requiresTaxDocument } from '../utils/verify-tax-step-label';
import { IWatrViewerDocument } from '../../utils/watr-viewer-document';

const FormWrapper = styled.div`
    .panel-body .control-label {
        color: #475569;
        font-weight: 600;
    }
    .panel-body .form-control {
        border-radius: 6px;
        border-color: #e2e8f0;
    }
    .panel-body .form-control:focus {
        border-color: #94a3b8;
        box-shadow: 0 0 0 2px rgba(148,163,184,0.2);
    }
    .input-group-addon {
        border-radius: 6px 0 0 6px;
        border-color: #e2e8f0;
        background: #f8fafc;
    }
    .selected-btn {
        border: 1px solid #3b82f6;
        background: #eff6ff;
        color: #1d4ed8;
    }
`;

const CardPanel = styled(Panel)`
    margin-top: 12px;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
    box-shadow: 0 1px 3px rgba(0,0,0,0.06);
    overflow: hidden;
`;

const PanelSubtitle = styled.p`
    margin: 0 0 20px 0;
    font-size: 14px;
    color: #64748b;
    font-weight: 500;
    letter-spacing: 0.01em;
`;

const SectionBlock = styled.div`
    margin-bottom: 20px;
`;

const SsnBlock = styled(SectionBlock)<{ $viewing?: boolean }>`
    padding: 14px 16px;
    background: ${p => p.$viewing ? '#eff6ff' : '#f8fafc'};
    border-radius: 8px;
    border: 1px solid ${p => p.$viewing ? '#93c5fd' : '#e2e8f0'};
    box-shadow: ${p => p.$viewing ? 'inset 3px 0 0 #3b82f6' : 'none'};
`;

const TaxBlock = styled(SectionBlock)<{ $viewing?: boolean }>`
    padding: 16px 18px;
    background: ${p => p.$viewing ? '#eff6ff' : '#fafbfc'};
    border-radius: 8px;
    border: 1px solid ${p => p.$viewing ? '#93c5fd' : '#e2e8f0'};
    box-shadow: ${p => p.$viewing ? 'inset 3px 0 0 #3b82f6' : 'none'};
`;

const TaxFieldsRow = styled.div`
    display: flex;
    flex-wrap: nowrap;
    align-items: flex-start;
    gap: 20px 28px;
`;

const ActionsRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    padding: 18px 20px;
    margin-top: 4px;
    background: #fafbfc;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
`;

const ViewActions = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
    margin-top: 12px;
`;

const REQUEST_FILE_TYPE_SSN = 8;

const hasSsnVerificationFile = (data: IWatrRequest): boolean =>
    (data.fileUploadRecords || []).some((r) => {
        const type = Number(r.fileType);
        return r.isActive !== false && !Number.isNaN(type) && type === REQUEST_FILE_TYPE_SSN;
    });

const numberedLabel = (step: number, name: string) => `${step} · ${name}`;

interface IProps {
    data: IWatrRequest,
    isAudit: boolean,
    reimbursements: IWatrReimbursement[],
    validation: IWageAndTravelVerifyValidation,
    isDirty: boolean,
}

const VerifyTax = (props: IProps) => {
    const [ssnDecrypted, setSsnDecrypted] = useState('');
    const [incomeIrsYear, setIncomeIrsYear] = useState(0);
    const [incomeIrsYearGross, setIncomeIrsYearGross] = useState(0);
    const [grossFocused, setGrossFocused] = useState(false);
    const [viewButton, setViewButton] = useState<number | null>(null);
    const fileLoadAttemptedForRequestId = useRef<number | null>(null);
    const context = useContext(DonorShieldContext);

    const hasTaxDoc = !!props.data.taxDocumentFile;
    const hasSsnDoc = hasSsnVerificationFile(props.data);
    const hasAffidavit = !!props.data.selfEmployedAffirmationFile;
    const requiresTaxDoc = requiresTaxDocument(props.data);

    const viewerDocs = useMemo((): IWatrViewerDocument[] => {
        const docs: IWatrViewerDocument[] = [];
        const requestId = props.data.id;
        let step = 1;

        if (hasSsnDoc) {
            docs.push({
                docType: FileDocumentType.SsnVerification,
                lookupId: requestId,
                label: numberedLabel(step, 'SS# Verification')
            });
            step += 1;
        }
        if (hasTaxDoc) {
            docs.push({
                docType: FileDocumentType.Tax,
                lookupId: requestId,
                label: numberedLabel(step, 'Tax Document')
            });
            step += 1;
        }
        if (hasAffidavit) {
            docs.push({
                docType: FileDocumentType.DsSelfEmployedAffidavit,
                lookupId: requestId,
                label: numberedLabel(step, 'Self Employed Affidavit')
            });
        }

        return docs;
    }, [props.data.id, hasSsnDoc, hasTaxDoc, hasAffidavit]);

    const docStep = useCallback((docType: number): number | null => {
        const index = viewerDocs.findIndex(d => d.docType === docType);
        return index >= 0 ? index + 1 : null;
    }, [viewerDocs]);

    const viewingSsn = viewButton === FileDocumentType.SsnVerification;
    const viewingTax = viewButton === FileDocumentType.Tax;
    const viewingAffidavit = viewButton === FileDocumentType.DsSelfEmployedAffidavit;
    const viewingTaxSection = viewingTax || viewingAffidavit;

    const handleViewerDocChange = useCallback((docType: number) => {
        setViewButton(docType);
    }, []);

    const openViewerAtIndex = useCallback(async (index: number) => {
        if (!viewerDocs.length || index < 0 || index >= viewerDocs.length) {
            return;
        }

        const doc = viewerDocs[index];
        const fileUrl = await context.fetchFileUrl(doc.docType, doc.lookupId);
        if (!fileUrl) {
            return;
        }

        setViewButton(doc.docType);
        context.onShowDataSideBar(
            doc.docType,
            doc.lookupId,
            fileUrl,
            viewerDocs.length,
            undefined,
            index,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            viewerDocs,
            handleViewerDocChange
        );
    }, [viewerDocs, context, handleViewerDocChange]);

    const handleViewFile = async (docType: number) => {
        const index = viewerDocs.findIndex(d => d.docType === docType);
        if (index < 0) {
            return;
        }

        await openViewerAtIndex(index);
    };

    useEffect(() => {
        setSsnDecrypted(props.data.ssnDecrypted);
        setIncomeIrsYear(props.data.incomeIrsYear);
        setIncomeIrsYearGross(props.data.incomeIrsYearGross ?? 0);

        const requestId = props.data.id;
        if (fileLoadAttemptedForRequestId.current === requestId) {
            return;
        }
        if (!viewerDocs.length) {
            return;
        }

        fileLoadAttemptedForRequestId.current = requestId;
        openViewerAtIndex(0);
    }, [props.data, viewerDocs, openViewerAtIndex]);

    const currentYear = new Date().getFullYear();

    const handleInputChange = (e: React.FormEvent<HTMLInputElement & FormControl>) => {
        let { name, value } = e.currentTarget;

        if (name === 'incomeIrsYear') {
            const digits = value.replace(/\D/g, '').slice(0, 4);
            const parsed = digits === '' ? 0 : parseInt(digits, 10);
            const year = isNaN(parsed) ? 0 : Math.min(parsed, currentYear);
            setIncomeIrsYear(year);
        }

        if (name === 'incomeIrsYearGross') {
            setIncomeIrsYearGross(parseMoneyInput(value));
        }
    };

    const showDeclineModal = () => {
        context.onShowDeclineModal(props.data.id, false);
    };

    const verifyTax = () => {
        context.onVerifyTax(props.data.id, ssnDecrypted, incomeIrsYear, incomeIrsYearGross, false);
    };

    const missingDocs: string[] = [];
    if (requiresTaxDoc && !hasTaxDoc) {
        missingDocs.push('tax document');
    }
    if (!hasSsnDoc) {
        missingDocs.push('SS# verification document');
    }

    return (
        <FormWrapper>
            <ConnectedDevMockDocsOffer
                data={props.data}
                downloadFile={(docType, lookupId) => context ? context.downloadFile(docType, lookupId as number) : Promise.resolve()}
                includeWages={true}
                includeReceipts={false}
                forceWageSlots={{ forceSsn: true, forceTax: true }}
                onComplete={() => {
                    fileLoadAttemptedForRequestId.current = null;
                    if (context.onRefreshRequest) {
                        context.onRefreshRequest();
                    }
                }}
            />
            <Header data={props.data} showStatus={false} />
            <VerificationStepTracker data={props.data} />

            <CardPanel>
                <Panel.Body style={{ padding: '20px 24px' }}>
                    <PanelSubtitle>
                        {requiresTaxDoc
                            ? 'Confirm SSN and tax information against both documents, then Verify or Decline.'
                            : 'Confirm SSN against the SS# verification document, then Verify or Decline.'}
                    </PanelSubtitle>

                    <SsnBlock $viewing={viewingSsn}>
                        <SsnInput
                            value={ssnDecrypted}
                            onChange={(value) => setSsnDecrypted(value)}
                            filesTaxesInUs={props.data.filesTaxesInUs}
                            isDirty={props.isDirty}
                            isValid={props.validation.ssnDecrypted}
                        />
                        <ViewActions>
                            <Button
                                className={viewButton === FileDocumentType.SsnVerification ? 'selected-btn' : ''}
                                disabled={!hasSsnDoc}
                                onClick={() => handleViewFile(FileDocumentType.SsnVerification)}
                            >
                                {numberedLabel(
                                    docStep(FileDocumentType.SsnVerification) || 1,
                                    viewingSsn ? 'Viewing SS# Document' : 'View SS# Document'
                                )}
                            </Button>
                            {!hasSsnDoc && (
                                <Alert bsStyle='warning' style={{ marginBottom: 0, borderRadius: 6, padding: '8px 12px' }}>
                                    SS# verification document not found.
                                </Alert>
                            )}
                        </ViewActions>
                    </SsnBlock>

                    {requiresTaxDoc && (
                        <TaxBlock $viewing={viewingTaxSection}>
                            {props.data.requireSelfEmployedAffirmation && (
                                <p style={{
                                    margin: '0 0 14px 0',
                                    fontSize: 13,
                                    color: '#64748b',
                                    lineHeight: 1.4
                                }}>
                                    Donor reported self-employed. Confirm Tax Year and Gross against the tax return
                                    {hasAffidavit ? ', and review the self-employed affidavit if needed.' : '.'}
                                </p>
                            )}

                            <TaxFieldsRow>
                                <FormGroup validationState={props.isDirty && !props.validation.incomeIrsYear ? 'error' : null} style={{ marginBottom: 0, flexShrink: 0 }}>
                                    <ControlLabel>Tax Year:&nbsp;</ControlLabel>
                                    <FormControl
                                        id='incomeIrsYear'
                                        name='incomeIrsYear'
                                        type='text'
                                        inputMode='numeric'
                                        maxLength={4}
                                        value={incomeIrsYear || ''}
                                        style={{ width: 100 }}
                                        onChange={handleInputChange} />
                                </FormGroup>

                                <FormGroup validationState={props.isDirty && !props.validation.incomeIrsYearGross ? 'error' : null} style={{ marginBottom: 0, flexShrink: 0 }}>
                                    <ControlLabel>Tax Year Gross:&nbsp;</ControlLabel>
                                    <InputGroup>
                                        <InputGroup.Addon>$</InputGroup.Addon>
                                        <FormControl
                                            id='incomeIrsYearGross'
                                            name='incomeIrsYearGross'
                                            type='text'
                                            inputMode='decimal'
                                            style={{ width: 130 }}
                                            value={grossFocused
                                                ? (incomeIrsYearGross ? String(incomeIrsYearGross) : '')
                                                : formatMoneyInput(incomeIrsYearGross)}
                                            onFocus={() => setGrossFocused(true)}
                                            onBlur={() => setGrossFocused(false)}
                                            onChange={handleInputChange} />
                                    </InputGroup>
                                </FormGroup>

                                <Alert bsStyle="info" style={{ marginTop: 20, marginBottom: 0, borderRadius: 6, flex: 1, minWidth: 200 }}>
                                    Confirm that the tax year gross amount shown matches the tax document before verifying.
                                </Alert>
                            </TaxFieldsRow>

                            <ViewActions>
                                <Button
                                    className={viewButton === FileDocumentType.Tax ? 'selected-btn' : ''}
                                    disabled={!hasTaxDoc}
                                    onClick={() => handleViewFile(FileDocumentType.Tax)}
                                >
                                    {numberedLabel(
                                        docStep(FileDocumentType.Tax) || 2,
                                        viewingTax ? 'Viewing Tax' : 'View Tax'
                                    )}
                                </Button>
                                {hasAffidavit && (
                                    <Button
                                        className={viewButton === FileDocumentType.DsSelfEmployedAffidavit ? 'selected-btn' : ''}
                                        onClick={() => handleViewFile(FileDocumentType.DsSelfEmployedAffidavit)}
                                    >
                                        {numberedLabel(
                                            docStep(FileDocumentType.DsSelfEmployedAffidavit) || 3,
                                            viewingAffidavit ? 'Viewing Affidavit' : 'Self Employed Affidavit'
                                        )}
                                    </Button>
                                )}
                                {!hasTaxDoc && (
                                    <Alert bsStyle='warning' style={{ marginBottom: 0, borderRadius: 6, padding: '8px 12px' }}>
                                        Tax document not found.
                                    </Alert>
                                )}
                            </ViewActions>
                        </TaxBlock>
                    )}

                    {missingDocs.length > 0 && (
                        <Alert bsStyle='danger' style={{ marginBottom: 0, borderRadius: 6 }}>
                            Cannot verify until the following are available: {missingDocs.join(', ')}.
                        </Alert>
                    )}
                </Panel.Body>
            </CardPanel>

            <ActionsRow>
                <Button bsStyle='primary' className='btn-danger' onClick={showDeclineModal} style={{ width: 110 }}>
                    Decline
                </Button>
                <Button
                    bsStyle='success'
                    onClick={verifyTax}
                    disabled={missingDocs.length > 0}
                    style={{ width: 110 }}
                >
                    Verify
                </Button>
            </ActionsRow>

            <hr />

            <WatrInfoAccordions
                data={props.data}
                isAudit={props.isAudit}
                reimbursements={props.reimbursements}
            />
        </FormWrapper>
    );
}

export default VerifyTax;
